import type { AiIconVariant } from "./aiDisclaimerIcons";
import {
  badgeAnchor,
  DEFAULT_AI_BADGE_POSITION,
  type AiBadgePosition,
} from "./aiBadgePosition";
import {
  AI_BADGE_GEOMETRY,
  DEFAULT_AI_BADGE_SIZE,
  type AiBadgeSize,
} from "./aiBadgeSize";

/**
 * How much of the image the whole-image fallback samples, as a fraction per axis.
 *
 * A quadrant, taken from whichever corner the badge is pinned to. The corner is a
 * parameter everywhere in this module rather than a constant, because a sample taken
 * from the wrong corner is worse than no sample: it answers confidently about pixels the
 * badge does not cover.
 */
export const BADGE_REGION = { x: 0.5, y: 0.5 } as const;

/**
 * Relative luminance of one sRGB pixel, per WCAG.
 *
 * The gamma-expanded form rather than a plain channel average: a mid-green and a mid-blue
 * of the same numeric value differ by more than 3x in perceived lightness, and a naive
 * average puts a saturated blue sky on the wrong side of the threshold.
 */
export function pixelLuminance(r: number, g: number, b: number): number {
  const channel = (value: number) => {
    const c = value / 255;
    return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };

  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

/**
 * Mean luminance over the badge's own corner of a raw RGB(A) buffer.
 *
 * Only that corner is sampled. A whole-image average is the wrong question: a dark photo
 * with a bright sky in the corner would resolve to "dark" and put a white badge on white
 * cloud.
 */
export function regionLuminance(
  pixels: Uint8Array | Uint8ClampedArray | number[],
  width: number,
  height: number,
  channels = 3,
  position: AiBadgePosition = DEFAULT_AI_BADGE_POSITION,
): number {
  const anchor = badgeAnchor(position);
  const spanX = Math.max(1, Math.floor(width * BADGE_REGION.x));
  const spanY = Math.max(1, Math.floor(height * BADGE_REGION.y));
  const startX = anchor.left ? 0 : width - spanX;
  const startY = anchor.top ? 0 : height - spanY;
  const endX = startX + spanX;
  const endY = startY + spanY;

  let total = 0;
  let count = 0;

  for (let y = startY; y < endY; y += 1) {
    for (let x = startX; x < endX; x += 1) {
      const i = (y * width + x) * channels;
      const r = pixels[i];
      const g = pixels[i + 1];
      const b = pixels[i + 2];

      if (r === undefined || g === undefined || b === undefined) continue;

      total += pixelLuminance(r, g, b);
      count += 1;
    }
  }

  return count === 0 ? 0 : total / count;
}

/**
 * The threshold between "treat this as a light image" and "treat it as dark".
 *
 * Biased below the midpoint on purpose. The two failure modes are not symmetric: a dark
 * pill on a mid-grey photo is merely low-contrast, while a white pill on the same photo
 * can be invisible — and an invisible badge is a missing compliance artifact, not a
 * cosmetic flaw. So mid-tones resolve to the dark pill.
 */
export const LIGHT_BACKGROUND_THRESHOLD = 0.4;

/** Which artwork to use for a measured background luminance. */
export function variantForLuminance(luminance: number): AiIconVariant {
  // `light` is the badge FOR a light image, so its own pill is dark.
  return luminance >= LIGHT_BACKGROUND_THRESHOLD ? "light" : "dark";
}

/**
 * A tiny variant of an asset, for sampling.
 *
 * 16px is enough: the value wanted is an average over a region, and the downscale
 * Storyblok performs is itself that average, done on their CDN instead of our server.
 */
export function iconSampleUrl(url: string, size = 16): string {
  if (!isStoryblokImage(url)) return url;

  // Appended to the base asset, never to whatever transform the browser was already
  // served: Storyblok cannot chain one `/m/` onto another, and the doubled url 404s.
  return `${storyblokAssetBase(url)}/m/${size}x${size}`;
}

function isStoryblokImage(url: string): boolean {
  if (!/^https:\/\/[a-z0-9-]+\.storyblok\.com\//i.test(url)) return false;

  // The image service does not transform SVGs; a `/m/` suffix on one 404s.
  return !/\.svg(\?|$)/i.test(url);
}

/**
 * The natural pixel dimensions Storyblok embeds in every asset url.
 *
 * `.../f/<space>/<width>x<height>/<hash>/<name>` — reading them from the url avoids
 * downloading the full-size original just to learn how big it is.
 */
export function parseStoryblokDimensions(
  url: string,
): { width: number; height: number } | null {
  const match = /\/(\d+)x(\d+)\//.exec(url);

  if (!match) return null;

  const width = Number(match[1]);
  const height = Number(match[2]);

  return width > 0 && height > 0 ? { width, height } : null;
}

/**
 * The part of a Storyblok url a transform may be appended to.
 *
 * The url that reaches the sampler is `currentSrc` — what the browser actually painted —
 * and for a Storyblok asset that is already transformed
 * (`.../m/1920x0/filters:format(avif)`). The image service does not chain transforms, so
 * anything we append has to go onto the bare asset path instead.
 */
export function storyblokAssetBase(url: string): string {
  const marker = url.indexOf("/m/");

  return marker === -1 ? url : url.slice(0, marker);
}

/**
 * The pixel size of the file the browser was actually served.
 *
 * Distinct from {@link parseStoryblokDimensions}, which reads the ORIGINAL's size out of
 * the asset path. A crop box derived from the original describes the wrong picture the
 * moment a transform resized or re-cropped it — and the loader puts a transform on every
 * Storyblok image, so that is the normal case rather than the exotic one.
 *
 * `0` on either axis is Storyblok's "derive this from the source aspect" sentinel, which
 * is what `storyblokLoader` emits for every image that does not opt into `ar`.
 */
export function storyblokDeliveredDimensions(
  url: string,
): { width: number; height: number } | null {
  const original = parseStoryblokDimensions(url);
  const marker = url.indexOf("/m/");

  if (marker === -1) return original;

  let source = original;
  let target: { width: number; height: number } | null = null;

  for (const segment of url.slice(marker + 3).split("/")) {
    // A crop segment (`left x top : right x bottom`) becomes the source the resize that
    // follows it applies to, so the aspect sentinel has to resolve against the crop.
    const crop = /^(\d+)x(\d+):(\d+)x(\d+)$/.exec(segment);

    if (crop) {
      source = {
        width: Number(crop[3]) - Number(crop[1]),
        height: Number(crop[4]) - Number(crop[2]),
      };
      continue;
    }

    const size = /^(\d+)x(\d+)$/.exec(segment);

    if (size) {
      target = { width: Number(size[1]), height: Number(size[2]) };
      break;
    }
  }

  if (!source || source.width <= 0 || source.height <= 0) return null;
  if (!target || (!target.width && !target.height)) return source;

  const aspect = source.width / source.height;
  const width = target.width || Math.round(target.height * aspect);
  const height = target.height || Math.round(target.width / aspect);

  return width > 0 && height > 0 ? { width, height } : null;
}

/**
 * How far the delivered file's aspect may drift from the original's before the fallback
 * crop stops meaning anything.
 *
 * The fallback expresses the badge box as a crop of the ORIGINAL, so it is only faithful
 * while the delivered file is a plain resize of it. An `ar=` transform or a `focal()`
 * crop moves the badge onto a different part of the picture, and no crop of the original
 * can describe that — the whole reason the measured-rect path exists.
 */
const ASPECT_TOLERANCE = 0.02;

/**
 * How far `AiDisclaimer` pins a default-size badge from its corner, in CSS pixels — see
 * `AI_BADGE_GEOMETRY`.
 */
export function badgeInset(size: AiBadgeSize): 12 | 28 {
  return AI_BADGE_GEOMETRY[size].inset;
}

/** How far a `compact` badge is pinned from its corner, in CSS pixels. */
export const COMPACT_BADGE_INSET = 4;

/** The thumbnail badge's height for a site-wide size — see `AI_BADGE_GEOMETRY`. */
export function compactBadgeSize(size: AiBadgeSize): 32 | 16 {
  return AI_BADGE_GEOMETRY[size].compact;
}

/** Width over height of the widest bound artwork — the ~300x95 pills. */
const BADGE_ARTWORK_ASPECT = 300 / 95;

/**
 * The badge's own footprint, in CSS pixels, for a badge `height` px tall.
 *
 * `AiDisclaimer` renders the artwork at the site's configured size pinned `badgeInset`
 * from its corner, and one inset of margin is included on the other two sides. Width
 * follows the widest bound artwork, so the box never undershoots a pill. It only has to
 * be close: this defines the region whose average tone is measured, and a few pixels
 * either way does not change which artwork wins.
 *
 * Computed from the size rather than kept as a literal because the size is now an editor
 * setting. A box larger than the badge averages in pixels the badge does not cover, and a
 * smaller one misses pixels it does — either way contrast is judged from the wrong region.
 */
export function badgeBox(
  height: AiBadgeSize,
  inset: number = badgeInset(height),
): { width: number; height: number } {
  return {
    width: Math.round(height * BADGE_ARTWORK_ASPECT) + inset * 2,
    height: height + inset * 2,
  };
}

/** The footprint at the default size — what a caller that cannot say otherwise assumes. */
export const BADGE_BOX = badgeBox(DEFAULT_AI_BADGE_SIZE);

/** The rendered width the badge geometry is calibrated against — a desktop hero. */
export const REFERENCE_RENDER_WIDTH = 1440;
/**
 * The badge's footprint in CSS pixels, per size, including one inset of margin.
 *
 * Derived from what the component actually renders — the configured size at its
 * `badgeInset`, or `compactBadgeSize` at a 4px inset for `compact` — because this is the region whose contrast
 * decides the artwork. Measuring the default box for a compact badge samples far more
 * than the badge covers: on a thumbnail narrower than that box it spans the whole image,
 * so the verdict comes from the entire picture rather than the corner under the badge.
 */
export function compactBadgeBox(size: AiBadgeSize): {
  width: number;
  height: number;
} {
  return badgeBox(compactBadgeSize(size), COMPACT_BADGE_INSET);
}

/** The compact footprint under the default size. */
export const BADGE_BOX_COMPACT = compactBadgeBox(DEFAULT_AI_BADGE_SIZE);

/**
 * A url that returns just the region the badge covers.
 *
 * Cropping on the CDN rather than averaging a corner of the whole image is what makes the
 * measurement match what a reader sees. The badge is ~5% of a 1440px-wide image, so a
 * quadrant average is dominated by the other 95%: an image that is light overall but dark
 * exactly under the badge — the case that actually hides a badge — reads as "light" and
 * gets the invisible artwork.
 *
 * The box is scaled by the DELIVERED file's size against a reference desktop render,
 * since a 4000px-wide file showing at 1440 devotes proportionally fewer of its own pixels
 * to the same badge — and then expressed in the original's pixels, because that is the
 * only url a crop can be appended to.
 *
 * `badge` is the footprint to crop, in CSS pixels at the reference width. It must follow
 * the size the badge renders at — the site-wide setting — or the crop covers a fraction
 * of a larger badge and judges its contrast from the pixels beside it.
 *
 * Returns `null` when the delivered file is a different shape from the original: the
 * badge then sits on pixels no crop of the original can name, and a wrong answer is worse
 * than the whole-image fallback.
 */
export function badgeCropUrl(
  url: string,
  size = 32,
  position: AiBadgePosition = DEFAULT_AI_BADGE_POSITION,
  badge: { width: number; height: number } = BADGE_BOX,
): string | null {
  if (!isStoryblokImage(url)) return null;

  const original = parseStoryblokDimensions(url);
  const delivered = storyblokDeliveredDimensions(url);

  if (!original || !delivered) return null;

  const originalAspect = original.width / original.height;
  const deliveredAspect = delivered.width / delivered.height;

  if (
    Math.abs(originalAspect - deliveredAspect) / originalAspect >
    ASPECT_TOLERANCE
  ) {
    return null;
  }

  // The badge box is CSS pixels against a reference render; `scale` puts it into the
  // delivered file's pixels, and `toOriginal` carries it back into the original's.
  const scale = Math.max(1, delivered.width / REFERENCE_RENDER_WIDTH);
  const toOriginal = original.width / delivered.width;

  const boxWidth = Math.min(
    original.width,
    Math.round(badge.width * scale * toOriginal),
  );
  const boxHeight = Math.min(
    original.height,
    Math.round(badge.height * scale * toOriginal),
  );

  const anchor = badgeAnchor(position);
  const left = anchor.left ? 0 : original.width - boxWidth;
  const top = anchor.top ? 0 : original.height - boxHeight;
  const right = left + boxWidth;
  const bottom = top + boxHeight;

  return `${storyblokAssetBase(url)}/m/${left}x${top}:${right}x${bottom}/${size}x${size}`;
}

/** Mean luminance of an entire raw RGB(A) buffer — used on an already-cropped region. */
export function meanLuminance(
  pixels: Uint8Array | Uint8ClampedArray | number[],
  channels = 3,
): number {
  let total = 0;
  let count = 0;

  for (let i = 0; i + channels - 1 < pixels.length; i += channels) {
    const r = pixels[i];
    const g = pixels[i + 1];
    const b = pixels[i + 2];

    if (r === undefined || g === undefined || b === undefined) continue;

    total += pixelLuminance(r, g, b);
    count += 1;
  }

  return count === 0 ? 0 : total / count;
}

/** How an `<img>` maps its source into its box. Mirrors CSS `object-fit`. */
export type ObjectFit = "fill" | "contain" | "cover" | "none" | "scale-down";

export type Rect = { x: number; y: number; width: number; height: number };

/**
 * The region of the source image the badge actually sits on, in source pixels.
 *
 * Sampling the source's own bottom-right corner is only correct when the whole image is
 * shown. It usually is not: `object-fit: cover` crops in the browser, and a Storyblok
 * transform at a different aspect crops before that. In both cases the corner the badge
 * lands on is a different part of the picture, so the contrast is measured somewhere the
 * reader never looks — and the badge gets the artwork that disappears on it.
 *
 * Given the natural size, the painted box and the fit, this maps the badge's rectangle
 * back through that transform. `positionX/Y` are `object-position` as fractions, so the
 * default 50%/50% is 0.5/0.5.
 */
export function badgeSourceRect({
  naturalWidth,
  naturalHeight,
  boxWidth,
  boxHeight,
  fit = "cover",
  positionX = 0.5,
  positionY = 0.5,
  badgeWidth = BADGE_BOX.width,
  badgeHeight = BADGE_BOX.height,
  position = DEFAULT_AI_BADGE_POSITION,
}: {
  naturalWidth: number;
  naturalHeight: number;
  boxWidth: number;
  boxHeight: number;
  fit?: ObjectFit;
  positionX?: number;
  positionY?: number;
  badgeWidth?: number;
  badgeHeight?: number;
  /** The corner the badge is pinned to, which is the corner that gets measured. */
  position?: AiBadgePosition;
}): Rect | null {
  if (
    naturalWidth <= 0 ||
    naturalHeight <= 0 ||
    boxWidth <= 0 ||
    boxHeight <= 0
  ) {
    return null;
  }

  const scaleX = boxWidth / naturalWidth;
  const scaleY = boxHeight / naturalHeight;

  let sx: number;
  let sy: number;

  if (fit === "fill") {
    sx = scaleX;
    sy = scaleY;
  } else if (fit === "contain") {
    sx = sy = Math.min(scaleX, scaleY);
  } else if (fit === "none") {
    sx = sy = 1;
  } else if (fit === "scale-down") {
    sx = sy = Math.min(1, Math.min(scaleX, scaleY));
  } else {
    sx = sy = Math.max(scaleX, scaleY);
  }

  const paintedWidth = naturalWidth * sx;
  const paintedHeight = naturalHeight * sy;
  // Where the painted image sits inside the box. Negative when it overflows, which is the
  // cropping case: the badge's corner then maps to somewhere inside the source.
  const offsetX = (boxWidth - paintedWidth) * positionX;
  const offsetY = (boxHeight - paintedHeight) * positionY;

  const toSource = (cx: number, cy: number) => ({
    x: (cx - offsetX) / sx,
    y: (cy - offsetY) / sy,
  });

  // The badge's rectangle in the box's own coordinates, pinned to its corner.
  const anchor = badgeAnchor(position);
  const boxLeft = anchor.left ? 0 : boxWidth - badgeWidth;
  const boxTop = anchor.top ? 0 : boxHeight - badgeHeight;

  const topLeft = toSource(boxLeft, boxTop);
  const bottomRight = toSource(boxLeft + badgeWidth, boxTop + badgeHeight);

  const clampX = (value: number) => Math.min(Math.max(value, 0), naturalWidth);
  const clampY = (value: number) => Math.min(Math.max(value, 0), naturalHeight);

  const x = clampX(topLeft.x);
  const y = clampY(topLeft.y);
  const width = clampX(bottomRight.x) - x;
  const height = clampY(bottomRight.y) - y;

  // A badge sitting entirely off the painted image — `contain` with heavy letterboxing —
  // has no pixels to measure. The caller falls back rather than sampling an empty rect.
  if (width < 1 || height < 1) return null;

  return { x, y, width, height };
}
